// Format date with time for display
import {CheckCircle, XCircle} from "lucide-react";
import {animations} from "./constants";
import React from "react";

export const formatDate = (dateString) => {
    if (!dateString) return "No date set";

    const options = {year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'};
    return new Date(dateString).toLocaleDateString(undefined, options);
};

// Format simple date for display (no time)
export const formatSimpleDate = (dateString) => {
    if (!dateString) return "No date";

    const options = {year: 'numeric', month: 'long', day: 'numeric'};
    return new Date(dateString).toLocaleDateString(undefined, options);
};

// Get initials for avatar
export const getInitials = (name) => {
    if (!name) return "?";
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

// Get CSS class for attendance rate
export const getAttendanceRateClass = (rate) => {
    if (rate >= 90) return "text-green-600";
    if (rate >= 80) return "text-yellow-600";
    return "text-red-600";
};

// Get CSS class for attendance rate background
export const getAttendanceRateBgClass = (rate) => {
    if (rate >= 90) return "bg-green-600";
    if (rate >= 80) return "bg-yellow-500";
    return "bg-red-500";
};

// Determine domain type from class info
export const getDomainType = (classItem) => {
    const name = classItem.name?.toLowerCase() || "";
    const desc = (classItem.description || "").toLowerCase();

    if (name.includes("physical") || desc.includes("physical"))
        return "Physical Development";
    if (name.includes("socio") || desc.includes("socio") || name.includes("emotion") || desc.includes("emotion"))
        return "Socio-Emotional Development";
    if (name.includes("cognitive") || desc.includes("cognitive"))
        return "Cognitive Development";
    if (name.includes("language") || desc.includes("language") || name.includes("literacy") || desc.includes("literacy"))
        return "Language & Literacy Development";
    if (name.includes("aesthetic") || desc.includes("aesthetic") || name.includes("cultural") || desc.includes("cultural"))
        return "Aesthetic & Cultural Development";
    if (name.includes("learning habits") || desc.includes("learning habits") || name.includes("positive") || desc.includes("positive"))
        return "Positive Learning Habits";

    // Extract domain number if present in the format "Domain X"
    const domainMatch = name.match(/domain\s*(\d+)/i) || desc.match(/domain\s*(\d+)/i);
    if (domainMatch) {
        const domainNum = parseInt(domainMatch[1]);
        switch (domainNum) {
            case 1:
                return "Physical Development";
            case 2:
                return "Socio-Emotional Development";
            case 3:
                return "Cognitive Development";
            case 4:
                return "Language & Literacy Development";
            case 5:
                return "Aesthetic & Cultural Development";
            default:
                return "Cognitive Development";
        }
    }

    // Default to cognitive if no match
    return "Cognitive Development";
};

// Format attendance status for display
export const formatAttendanceStatus = (present) => {
    if (present) {
        return (
            <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle size={14} className="mr-1"/>
                Present
            </span>
        );
    } else {
        return (
            <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <XCircle size={14} className="mr-1"/>
                Absent
            </span>
        );
    }
};

// Add CSS animations to document
export const addAnimationsToDocument = () => {
    if (typeof document !== 'undefined') {
        const styleElement = document.createElement('style');
        styleElement.innerText = animations;
        document.head.appendChild(styleElement);
    }
};

export const getDomainConfig = (domainNameRaw) => {
    const domainName = (domainNameRaw || "").toLowerCase();

    if (domainName.includes("physical")) {
        return {
            icon: <img src="https://cdn-icons-png.flaticon.com/512/18320/18320927.png" alt="Physical-Development"
                       className="w-5 h-5" style={{filter: "invert(1)"}}/>,
            image: "https://images.pexels.com/photos/8430599/pexels-photo-8430599.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
            gradient: "from-orange-400 to-red-500",
            color: "#FF7A59"
        };
    }
    if (domainName.includes("socio") || domainName.includes("emotional")) {
        return {
            icon: <img src="https://cdn-icons-png.flaticon.com/512/12649/12649548.png"
                       alt="Socio-Emotional-Development" className="w-5 h-5" style={{filter: "invert(1)"}}/>,
            image: "https://images.pexels.com/photos/8385033/pexels-photo-8385033.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
            gradient: "from-pink-400 to-orange-500",
            color: "#F17C45"
        };
    }
    if (domainName.includes("cognitive")) {
        return {
            icon: <img src="https://cdn-icons-png.flaticon.com/512/3288/3288930.png" alt="Cognitive-Development"
                       className="w-5 h-5" style={{filter: "invert(1)"}}/>,
            image: "https://images.pexels.com/photos/7296429/pexels-photo-7296429.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
            gradient: "from-blue-400 to-orange-500",
            color: "#1D3469"
        };
    }
    if (domainName.includes("literacy") || domainName.includes("language")) {
        return {
            icon: <img src="https://cdn-icons-png.flaticon.com/512/3898/3898150.png"
                       alt="Language-Literacy-Development" className="w-5 h-5" style={{filter: "invert(1)"}}/>,
            image: "https://images.pexels.com/photos/8613121/pexels-photo-8613121.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
            gradient: "from-green-400 to-orange-500",
            color: "#FF7A59"
        };
    }
    if (domainName.includes("aesthetic") || domainName.includes("cultural")) {
        return {
            icon: <img src="https://cdn-icons-png.flaticon.com/512/5730/5730993.png"
                       alt="Aesthetic-Cultural-Development" className="w-5 h-5" style={{filter: "invert(1)"}}/>,
            image: "https://images.pexels.com/photos/8819206/pexels-photo-8819206.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
            gradient: "from-purple-400 to-orange-500",
            color: "#F17C45"
        };
    }
    if (domainName.includes("positive") || domainName.includes("habit")) {
        return {
            icon: <img src="https://cdn-icons-png.flaticon.com/512/4310/4310163.png" alt="Positive-Learning-Habits"
                       className="w-5 h-5" style={{filter: "invert(1)"}}/>,
            image: "https://images.pexels.com/photos/4546115/pexels-photo-4546115.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
            gradient: "from-teal-400 to-orange-500",
            color: "#FF7A59"
        };
    }
    // fallback:
    return {
        icon: <img src="https://cdn-icons-png.flaticon.com/512/18320/18320927.png" alt="Physical-Development"
                   className="w-5 h-5" style={{filter: "invert(1)"}}/>,
        image: "https://images.pexels.com/photos/8430599/pexels-photo-8430599.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
        gradient: "from-orange-400 to-red-500",
        color: "#FF7A59"
    };
};
